import { useEffect, useState } from "react";
import {
  CBadge,
  CButton,
  CButtonGroup,
  CFormGroup,
  CLabel,
  CSelect,
  CTextarea,
} from "@coreui/react";
import { getItem, getList, postItem } from "../../api/generics";
import TaxPayer from "../../models/tax-payer";
import {
  SuspensionReason,
  TaxPayerSuspensionsData,
  suspensionUserLabel,
} from "../../models/tax-payer-suspension";
import { SUCCESS } from "../../utils/constants/tags";
import Loading from "../indicators/Loading";
import { warningAlert } from "../utils/messages";
import ConfirmModal from "../utils/ConfirmModal";

interface Props {
  taxPayer: TaxPayer;
  onCancel: () => void;
  onSuccess: (suspended: boolean) => void;
  onBusyChange?: (busy: boolean) => void;
}

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : "-";

const TaxPayerSuspensionForm = ({
  taxPayer,
  onCancel,
  onSuccess,
  onBusyChange,
}: Props) => {
  const [data, setData] = useState<TaxPayerSuspensionsData>();
  const [reasons, setReasons] = useState<SuspensionReason[]>([]);
  const [reasonId, setReasonId] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [retry, setRetry] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const url = `/tax_payers/${taxPayer.id}/suspensions/`;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");
    const load = async () => {
      try {
        const [suspensions, reasonList] = await Promise.all([
          getItem<TaxPayerSuspensionsData>(url),
          getList<SuspensionReason>("/tax_payers/suspension_reasons/", 100, 0),
        ]);
        if (!active) return;
        if (
          suspensions.status !== SUCCESS ||
          !suspensions.data ||
          reasonList.status !== SUCCESS ||
          !reasonList.data
        ) {
          setLoadError(
            suspensions.detail ||
              reasonList.detail ||
              "No se pudo cargar el estado de suspensión.",
          );
          return;
        }
        setData(suspensions.data);
        setReasons(reasonList.data.items);
        setReasonId(
          reasonList.data.items.length > 0
            ? String(reasonList.data.items[0].id)
            : "",
        );
      } catch {
        if (active) setLoadError("No se pudo cargar el estado de suspensión.");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [url, retry]);

  const suspending = data?.active === null;
  const name = taxPayer.socialName || taxPayer.ruc;

  const submit = async () => {
    setConfirming(false);
    if (!data || submitting) return;

    setSubmitting(true);
    onBusyChange?.(true);
    let done = false;
    try {
      const result = suspending
        ? await postItem(url, {
            suspensionReasonId: Number(reasonId),
            notes: notes.trim() || null,
          })
        : await postItem(`${url}lift/`, { notes: notes.trim() || null });
      if (result.status === SUCCESS) {
        done = true;
      } else {
        const fieldError = Object.values(result.errors ?? {})
          .flat()
          .find((error): error is string => typeof error === "string");
        warningAlert(
          result.detail || fieldError || "No se pudo guardar el cambio.",
        );
      }
    } catch {
      warningAlert("No se pudo guardar el cambio.");
    } finally {
      setSubmitting(false);
      onBusyChange?.(false);
    }
    if (done) onSuccess(suspending);
  };

  if (loading) return <Loading height="20vh" />;

  if (loadError || !data) {
    return (
      <>
        <div className="alert alert-danger" role="alert">
          {loadError}
        </div>
        <div className="d-flex justify-content-end">
          <CButtonGroup>
            <CButton color="secondary" onClick={onCancel}>
              Salir
            </CButton>
            <CButton
              color="primary"
              onClick={() => setRetry((value) => value + 1)}
            >
              Reintentar
            </CButton>
          </CButtonGroup>
        </div>
      </>
    );
  }

  const active = data.active;

  return (
    <>
      {active ? (
        <div className="alert alert-danger" role="status">
          <strong>Suspendido</strong> por {active.reasonName} desde el{" "}
          {formatDate(active.suspendedAt)}, por{" "}
          {suspensionUserLabel(active.suspendedBy)}.
          {active.notes && <div className="mt-1">Notas: {active.notes}</div>}
        </div>
      ) : (
        <>
          <div className="alert alert-success" role="status">
            El contribuyente está activo.
          </div>
          <CFormGroup>
            <CLabel htmlFor="suspension-reason">Motivo</CLabel>
            <CSelect
              id="suspension-reason"
              value={reasonId}
              disabled={submitting}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                setReasonId(event.target.value)
              }
            >
              {reasons.map((reason) => (
                <option key={reason.id} value={reason.id}>
                  {reason.name}
                </option>
              ))}
            </CSelect>
          </CFormGroup>
        </>
      )}
      <CFormGroup>
        <CLabel htmlFor="suspension-notes">
          {active ? "Notas de reactivación" : "Notas"} (opcional)
        </CLabel>
        <CTextarea
          id="suspension-notes"
          rows={2}
          value={notes}
          disabled={submitting}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
            setNotes(event.target.value)
          }
        />
      </CFormGroup>
      <div className="d-flex justify-content-end mb-3">
        <CButtonGroup>
          <CButton color="secondary" onClick={onCancel} disabled={submitting}>
            Salir
          </CButton>
          {active ? (
            <CButton
              color="success"
              onClick={() => setConfirming(true)}
              disabled={submitting}
            >
              {submitting ? "Reactivando..." : "Reactivar"}
            </CButton>
          ) : (
            <CButton
              color="danger"
              onClick={() => setConfirming(true)}
              disabled={submitting || reasonId === ""}
            >
              {submitting ? "Suspendiendo..." : "Suspender"}
            </CButton>
          )}
        </CButtonGroup>
      </div>
      <ConfirmModal
        show={confirming}
        title={suspending ? "Suspender contribuyente" : "Reactivar contribuyente"}
        message={
          suspending
            ? `¿Suspender a ${name}? Sus usuarios no podrán ver ni emitir documentos hasta que se reactive.`
            : `¿Reactivar a ${name}? Sus usuarios recuperarán el acceso de inmediato.`
        }
        confirmLabel={suspending ? "Suspender" : "Reactivar"}
        color={suspending ? "danger" : "success"}
        onConfirm={submit}
        onCancel={() => setConfirming(false)}
      />
      {data.items.length > 0 && (
        <>
          <h6>Historial</h6>
          <div className="table-responsive">
            <table className="table table-sm table-bordered mb-0">
              <thead>
                <tr>
                  <th>Motivo</th>
                  <th>Suspendido</th>
                  <th>Reactivado</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.reasonName}
                      {item.notes && (
                        <div>
                          <small className="text-muted">{item.notes}</small>
                        </div>
                      )}
                    </td>
                    <td>
                      {formatDate(item.suspendedAt)}
                      <div>
                        <small className="text-muted">
                          {suspensionUserLabel(item.suspendedBy)}
                        </small>
                      </div>
                    </td>
                    <td>
                      {item.liftedAt ? (
                        <>
                          {formatDate(item.liftedAt)}
                          <div>
                            <small className="text-muted">
                              {suspensionUserLabel(item.liftedBy)}
                            </small>
                          </div>
                          {item.liftNotes && (
                            <div>
                              <small className="text-muted">
                                {item.liftNotes}
                              </small>
                            </div>
                          )}
                        </>
                      ) : (
                        <CBadge color="danger">Vigente</CBadge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
};

export default TaxPayerSuspensionForm;
